import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { chatAPI } from '../../services/api';

export const fetchChats = createAsyncThunk('chats/fetchChats', async (_, { rejectWithValue }) => {
  try {
    const response = await chatAPI.getChats();
    return response.data.chats;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch chats');
  }
});

export const fetchChatMessages = createAsyncThunk('chats/fetchChatMessages', async ({ chatId, params }, { rejectWithValue }) => {
  try {
    const response = await chatAPI.getChat(chatId, params);
    return { chatId, ...response.data };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch messages');
  }
});

// Fetch unread message count
export const fetchUnreadCount = createAsyncThunk('chats/fetchUnreadCount', async (_, { rejectWithValue }) => {
  try {
    const response = await chatAPI.getUnreadCount();
    return response.data.unreadCount;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch unread count');
  }
});

// Fetch list of unread messages
export const fetchUnreadMessages = createAsyncThunk('chats/fetchUnreadMessages', async (_, { rejectWithValue }) => {
  try {
    const response = await chatAPI.getUnreadMessages();
    return response.data.messages;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch unread messages');
  }
});

// Mark chat messages as read
export const markChatAsRead = createAsyncThunk('chats/markChatAsRead', async (chatId, { rejectWithValue, dispatch }) => {
  try {
    const response = await chatAPI.markChatAsRead(chatId);
    // Refresh unread count after marking as read
    dispatch(fetchUnreadCount());
    return { chatId, markedCount: response.data.markedCount };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to mark chat as read');
  }
});

// Send bulk message to multiple members (trainers only)
export const sendBulkMessage = createAsyncThunk('chats/sendBulkMessage', async ({ memberIds, content, attachmentUrl }, { rejectWithValue }) => {
  try {
    const response = await chatAPI.sendBulkMessage(memberIds, content, attachmentUrl);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to send bulk message');
  }
});

// Fetch all chats with members for inbox view (trainers only)
export const fetchAllChatsWithMembers = createAsyncThunk('chats/fetchAllChatsWithMembers', async (_, { rejectWithValue }) => {
  try {
    const response = await chatAPI.getAllChatsWithMembers();
    return response.data.members;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch members with chats');
  }
});

const chatSlice = createSlice({
  name: 'chats',
  initialState: {
    chats: [],
    activeChat: null,
    messages: [],
    unreadCount: 0,
    unreadMessages: [],
    membersWithChats: [],
    bulkSending: false,
    loading: false,
    error: null,
  },
  reducers: {
    setActiveChat: (state, action) => { state.activeChat = action.payload; },
    addMessage: (state, action) => { state.messages.push(action.payload); },
    clearMessages: (state) => { state.messages = []; },
    clearUnreadMessages: (state) => { state.unreadMessages = []; },
    setUnreadCount: (state, action) => { state.unreadCount = action.payload; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChats.fulfilled, (state, action) => { state.chats = action.payload; })
      .addCase(fetchChatMessages.fulfilled, (state, action) => { state.messages = action.payload.messages; })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => { state.unreadCount = action.payload; })
      .addCase(fetchUnreadMessages.pending, (state) => { state.loading = true; })
      .addCase(fetchUnreadMessages.fulfilled, (state, action) => { 
        state.unreadMessages = action.payload; 
        state.loading = false;
      })
      .addCase(fetchUnreadMessages.rejected, (state) => { state.loading = false; })
      .addCase(markChatAsRead.fulfilled, (state, action) => {
        // Remove messages from that chat from unreadMessages
        state.unreadMessages = state.unreadMessages.filter(msg => msg.chatId !== action.payload.chatId);
      })
      // Bulk message handlers
      .addCase(sendBulkMessage.pending, (state) => { state.bulkSending = true; })
      .addCase(sendBulkMessage.fulfilled, (state) => { state.bulkSending = false; })
      .addCase(sendBulkMessage.rejected, (state) => { state.bulkSending = false; })
      // Members with chats (for inbox)
      .addCase(fetchAllChatsWithMembers.pending, (state) => { state.loading = true; })
      .addCase(fetchAllChatsWithMembers.fulfilled, (state, action) => { 
        state.membersWithChats = action.payload; 
        state.loading = false;
      })
      .addCase(fetchAllChatsWithMembers.rejected, (state) => { state.loading = false; });
  },
});

export const { setActiveChat, addMessage, clearMessages, clearUnreadMessages, setUnreadCount } = chatSlice.actions;
export const selectChats = (state) => state.chat?.chats || [];
export const selectActiveChat = (state) => state.chat?.activeChat;
export const selectMessages = (state) => state.chat?.messages || [];
export const selectUnreadCount = (state) => state.chat?.unreadCount || 0;
export const selectUnreadMessages = (state) => state.chat?.unreadMessages || [];
export const selectChatLoading = (state) => state.chat?.loading || false;
export const selectMembersWithChats = (state) => state.chat?.membersWithChats || [];
export const selectBulkSending = (state) => state.chat?.bulkSending || false;
export default chatSlice.reducer;
